import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronUpIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { capitalize } from 'lodash/fp';
import { MouseEvent, ReactElement, useEffect, useState, VFC } from 'react';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import { StyledInput } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ExpressionEditor/Term';
import ExpressionTermConfig, {
  REACT_SELECT_STYLES,
  ReactSelectContainer,
  Row,
  SideLabel,
} from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ExpressionEditor/TermConfig';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  ExpressionVar,
  generateArrayRule,
  generateExpressionRule,
  getExpressionRuleParts,
  getOperatorOptions,
  JsonLogicField,
  RULE_VALUE_TYPE_OPTIONS,
  RuleValueTypes,
  VARIABLE_TYPE_TO_OPERATORS,
} from 'dashboard/lib/workflows';
import { WorkflowError, WorkflowOperatorKey } from 'dashboard/models/workflow';
import { VariableConfig, VariableType } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { TransitionDuration } from 'design-system/variables/transitions';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

const StyledRow = styled(Row)`
  border-top: 1px solid ${Color.CoreSlate400};
  padding-top: ${Spacing[4]};
  margin-top: ${Spacing[4]};
`;

const ClickableRow = styled(Row)`
  cursor: pointer;
`;

const TYPE_OPTIONS = Object.keys(VARIABLE_TYPE_TO_OPERATORS).map((type) => ({
  label: capitalize(type),
  value: type as VariableType,
}));

const NameInput = styled.div`
  flex: 2;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1;
`;

const ErrorContainer = styled.div`
  margin-left: 200px;
`;

const StyledChevronIcon = styled(ChevronUpIcon)<{ expanded: boolean }>`
  transform: rotate(${({ expanded }) => (expanded ? '0' : '-180deg')});
  transition: transform ${TransitionDuration.Fast};
`;

const WithTooltip = ({
  showTooltip,
  message,
  trigger,
}: {
  showTooltip: boolean;
  message: string;
  trigger: ReactElement;
}) =>
  showTooltip ? (
    <Tooltip side='top' hideArrow trigger={<div>{trigger}</div>}>
      <Paragraph>{message}</Paragraph>
    </Tooltip>
  ) : (
    trigger
  );

interface VariableEditorProps {
  deletable: boolean;
  errors: WorkflowError[];
  expanded: boolean;
  isReferenced: boolean;
  jsonSchema: JsonSchema;
  nameError: boolean;
  tokensToDisplayValue: Record<string, string>;
  variable: VariableConfig;
  variableSchema: JsonSchema;
  varTokensToDisplayValue: Record<string, string>;
  onDelete: () => void;
  setConfig: (config: Partial<VariableConfig>) => void;
  toggleExpanded: () => void;
}
const VariableEditor: VFC<VariableEditorProps> = ({
  deletable,
  errors,
  expanded,
  isReferenced,
  jsonSchema,
  nameError,
  tokensToDisplayValue,
  variable,
  variableSchema,
  varTokensToDisplayValue,
  onDelete,
  setConfig,
  toggleExpanded,
}) => {
  const { id, type, value, name } = variable;
  const { operator, valueLeftConfig, valueRightConfig, valueDefaultConfig } =
    getExpressionRuleParts(value);
  const [selectedOperator, setSelectedOperator] = useState(operator);
  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError(errors);

  useEffect(() => setSelectedOperator(operator), [operator]);

  const onChangeOperator = (newOperator: WorkflowOperatorKey) => {
    setSelectedOperator(newOperator);

    // Note: higher order operators like SUM/AVG are implemented as
    // a combination of other base operators. For display purposes, we
    // use the operator keys normally here, but when the rule actually
    // gets saved it will be replaced with the appropriate json via generateArrayRule
    setConfig({
      type,
      name,
      value: {
        [newOperator]: [],
      },
    });
  };

  const onToggleExpand = (e: MouseEvent) => {
    e.stopPropagation();
    toggleExpanded();
  };

  const renderDefault = (currentOperator: WorkflowOperatorKey) => {
    if (type !== JsonSchemaTypes.Number) {
      return;
    }
    return (
      <>
        <Spacer height={1} />
        <ExpressionTermConfig
          id='expression-editor__term-default'
          variableType={type}
          hasError={hasErrorFor(`${id}-default`)}
          jsonSchema={jsonSchema}
          label='Default'
          operator={currentOperator}
          isDefault
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          termConfig={valueDefaultConfig}
          termOptions={RULE_VALUE_TYPE_OPTIONS}
          tokensToDisplayValue={tokensToDisplayValue}
          variableSchema={variableSchema}
          varTokensToDisplayValue={varTokensToDisplayValue}
          setVariableValue={(newVal) =>
            setConfig({
              type,
              name,
              value: generateExpressionRule(
                {
                  [currentOperator]: [valueLeftConfig?.value, valueRightConfig?.value],
                },
                newVal,
              ),
            })
          }
        />
      </>
    );
  };

  const renderNested = (currentOperator: WorkflowOperatorKey) => {
    switch (currentOperator) {
      case WorkflowOperatorKey.Sum:
      case WorkflowOperatorKey.Avg: {
        const termOptions = RULE_VALUE_TYPE_OPTIONS.filter(
          (option) => option.value !== RuleValueTypes.Value,
        );
        return (
          <>
            <ExpressionTermConfig
              id='expression-editor__term-left'
              variableType={type}
              hasError={hasErrorFor(`${id}-left`)}
              jsonSchema={jsonSchema}
              label='Array'
              operator={currentOperator}
              termConfig={valueLeftConfig}
              termOptions={termOptions}
              tokensToDisplayValue={tokensToDisplayValue}
              variableSchema={variableSchema}
              varTokensToDisplayValue={varTokensToDisplayValue}
              setVariableValue={(newVal) => {
                if (newVal) {
                  setConfig({
                    type,
                    name,
                    value: generateExpressionRule(
                      generateArrayRule(currentOperator, newVal as JsonLogicField | ExpressionVar),
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      valueDefaultConfig?.value,
                    ),
                  });
                }
              }}
            />
            <ErrorContainer data-test='variable-editor__error-container'>
              {renderErrorMessageFor(`variables.${id}-left`)}
            </ErrorContainer>

            {renderDefault(currentOperator)}
          </>
        );
      }
      case WorkflowOperatorKey.Add:
      case WorkflowOperatorKey.Subtract:
      case WorkflowOperatorKey.Multiply:
      case WorkflowOperatorKey.Divide:
      case WorkflowOperatorKey.Concat:
      case WorkflowOperatorKey.Equals:
      case WorkflowOperatorKey.DoesNotEqual:
      case WorkflowOperatorKey.GreaterThan:
      case WorkflowOperatorKey.GreaterThanOrEqual:
      case WorkflowOperatorKey.LessThan:
      case WorkflowOperatorKey.LessThanOrEqual:
      case WorkflowOperatorKey.StartsWith:
      case WorkflowOperatorKey.And:
      case WorkflowOperatorKey.Or: {
        return (
          <>
            <ExpressionTermConfig
              id='expression-editor__term-left'
              variableType={type}
              hasError={hasErrorFor(`${id}-left`)}
              jsonSchema={jsonSchema}
              label='Left'
              operator={currentOperator}
              termConfig={valueLeftConfig}
              termOptions={RULE_VALUE_TYPE_OPTIONS}
              tokensToDisplayValue={tokensToDisplayValue}
              variableSchema={variableSchema}
              varTokensToDisplayValue={varTokensToDisplayValue}
              setVariableValue={(newVal) =>
                setConfig({
                  type,
                  name,
                  value: generateExpressionRule(
                    {
                      [currentOperator]: [newVal, valueRightConfig?.value],
                    },
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    valueDefaultConfig?.value,
                  ),
                })
              }
            />
            <ErrorContainer data-test='variable-editor__error-container'>
              {renderErrorMessageFor(`variables.${id}-left`)}
            </ErrorContainer>

            <Spacer height={1} />
            <ExpressionTermConfig
              id='expression-editor__term-right'
              variableType={type}
              hasError={hasErrorFor(`${id}-right`)}
              jsonSchema={jsonSchema}
              label='Right'
              operator={currentOperator}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              termConfig={valueRightConfig}
              termOptions={RULE_VALUE_TYPE_OPTIONS}
              tokensToDisplayValue={tokensToDisplayValue}
              variableSchema={variableSchema}
              varTokensToDisplayValue={varTokensToDisplayValue}
              setVariableValue={(newVal) =>
                setConfig({
                  type,
                  name,
                  value: generateExpressionRule(
                    {
                      [currentOperator]: [valueLeftConfig?.value, newVal],
                    },
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    valueDefaultConfig?.value,
                  ),
                })
              }
            />
            <ErrorContainer data-test='variable-editor__error-container'>
              {renderErrorMessageFor(`variables.${id}-right`)}
            </ErrorContainer>

            {renderDefault(currentOperator)}
          </>
        );
      }
      case WorkflowOperatorKey.IsBlank:
      case WorkflowOperatorKey.IsNotBlank: {
        return (
          <>
            <ExpressionTermConfig
              id='expression-editor__term-left'
              variableType={type}
              hasError={hasErrorFor(`${id}-left`)}
              jsonSchema={jsonSchema}
              label='Text'
              operator={currentOperator}
              termConfig={valueLeftConfig}
              termOptions={RULE_VALUE_TYPE_OPTIONS}
              tokensToDisplayValue={tokensToDisplayValue}
              variableSchema={variableSchema}
              varTokensToDisplayValue={varTokensToDisplayValue}
              setVariableValue={(newVal) =>
                setConfig({
                  type,
                  name,
                  value: generateExpressionRule(
                    {
                      [currentOperator]: [newVal],
                    },
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    valueDefaultConfig?.value,
                  ),
                })
              }
            />
            <ErrorContainer data-test='variable-editor__error-container'>
              {renderErrorMessageFor(`variables.${id}-left`)}
            </ErrorContainer>
            {renderDefault(currentOperator)}
          </>
        );
      }
    }
  };

  return (
    <div data-test='variable-editor__container'>
      {/* for some reason adding an id here makes the spec fail?? */}
      <ClickableRow onClick={onToggleExpand}>
        <SideLabel htmlFor='variable-editor__variable'>Variable</SideLabel>
        <ReactSelectContainer
          id='variable-editor__variable-type-container'
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          <WithTooltip
            showTooltip={isReferenced}
            message='Remove all references to this variable before changing type'
            trigger={
              <ReactSelect
                id='variable-editor__variable-type'
                aria-label='Select variable type'
                disabled={isReferenced}
                hasError={hasErrorFor(`${id}-type`)}
                value={type}
                options={TYPE_OPTIONS}
                styles={REACT_SELECT_STYLES}
                isSearchable={false}
                onChange={(newType: VariableType) => {
                  const newOperator = VARIABLE_TYPE_TO_OPERATORS[newType][0];
                  setConfig({
                    type: newType,
                    name,
                    value: {
                      [newOperator]: [],
                    },
                  });
                }}
              />
            }
          />
        </ReactSelectContainer>
        <NameInput
          id='variable-editor__variable-name'
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          <WithTooltip
            showTooltip={isReferenced}
            message='Remove all references to this variable before renaming'
            trigger={
              <StyledInput
                id='variable-editor__variable'
                aria-label='Enter variable name'
                aria-invalid={nameError}
                hasError={nameError}
                disabled={isReferenced}
                value={name}
                onChange={(e) =>
                  setConfig({
                    type,
                    value,
                    name: e.target.value,
                  })
                }
              />
            }
          />
        </NameInput>
        <ButtonContainer>
          <WithTooltip
            showTooltip={isReferenced}
            message='Remove all references to this variable before deleting'
            trigger={
              <Button
                id='variable-editor__remove'
                aria-label='Delete variable'
                variant='basic'
                width='icon'
                disabled={!deletable}
                onClick={onDelete}
              >
                <TrashIcon />
              </Button>
            }
          />
          <Spacer height={0} width={1} />
          <Button
            id='variable-editor__expand'
            aria-label='Expand variable'
            variant='basic'
            width='icon'
            onClick={onToggleExpand}
          >
            <StyledChevronIcon expanded={expanded} />
          </Button>
        </ButtonContainer>
      </ClickableRow>
      <ErrorContainer data-test='variable-editor__error-container'>
        {renderErrorMessageFor(`variables.${id}-name`)}
      </ErrorContainer>
      {expanded && (
        <div data-test='variable-editor__config'>
          <StyledRow>
            <SideLabel htmlFor='variable-editor__operator-input'>Operator</SideLabel>
            <ReactSelectContainer>
              <ReactSelect
                id='variable-editor__operator'
                hasError={hasErrorFor(`${id}-operator`)}
                value={selectedOperator}
                options={getOperatorOptions(type)}
                styles={REACT_SELECT_STYLES}
                isSearchable={false}
                onChange={onChangeOperator}
              />
            </ReactSelectContainer>
          </StyledRow>
          <ErrorContainer data-test='variable-editor__error-container'>
            {renderErrorMessageFor(`variables.${id}-operator`)}
          </ErrorContainer>
          <Spacer height={1} />
          {renderNested(selectedOperator)}
        </div>
      )}
    </div>
  );
};

export default VariableEditor;
